<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2021-2024 (original work) Open Assessment Technologies SA ;

    /**
     * FloatingBar component, to use in floating toolbars.
     * Contains slot for action buttons, and another for footer.
     * IconBarButton with `base-16` size and `circular` shape is intended to be used here, but can also contain other components.
     * Does not position itself on page, should be positioned by parent component.
     * @property {String} [title] - header text
     * @property {String} [ariaLabel] - header aria label
     */
    export let title;
    export let ariaLabel;
</script>

<style>
    .floating-bar {
        --floating-bar-height: 5.5rem;
        border-radius: calc(var(--floating-bar-height) / 2);
        pointer-events: auto; /* might be needed if container uses pointer-events:none */

        & .floating-bar-main {
            height: var(--floating-bar-height);
            display: inline-flex;

            & header {
                flex: 1 1 auto;
                display: flex;
                align-items: center;
                padding: 0 1.5rem;
                text-transform: uppercase;
                font-size: var(--fontsize-body-s);
                font-weight: bold;
                user-select: none;
                white-space: nowrap;
                overflow: hidden;
            }

            & .floating-bar-actions {
                flex: 0 0 auto;
                display: flex;
                align-items: center;
                padding: 0 0.25rem;

                /* override IconBarButton styles, if it's used as action */
                & :global-nested(.icon-bar-btn) {
                    height: 5rem;
                    width: 5rem;
                    margin: 0;
                }
            }
        }
        & footer {
            border-bottom-left-radius: calc(var(--floating-bar-height) / 2);
            border-bottom-right-radius: calc(var(--floating-bar-height) / 2);
        }
    }
</style>

<div class="floating-bar inverted" role="toolbar" aria-label={ariaLabel || title}>
    <div class="floating-bar-main">
        {#if title}
            <header>{title}</header>
        {/if}
        <div class="floating-bar-actions">
            <slot />
        </div>
    </div>
    {#if $$slots.footer}
        <footer>
            <slot name="footer" />
        </footer>
    {/if}
</div>
